import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getSellerBySubdomain, getMySellerInfo } from '../services/api';
import { useAuth } from './AuthContext';
import { applyThemeVars, clearThemeVars } from '../utils/themeColors';

const SellerContext = createContext();

export const useSeller = () => {
  const context = useContext(SellerContext);
  if (!context) {
    throw new Error('useSeller must be used within a SellerProvider');
  }
  return context;
};

// 서브도메인 감지 로직 (컴포넌트 외부로 분리)
const detectSubdomain = () => {
    if (typeof window === 'undefined') return null;
    
    const host = window.location.hostname;
    
    // localhost는 서브도메인 없음, 개발 서버 도메인도 제외
    if (host === 'localhost' || host === '127.0.0.1' || host === 'dev-linkflower.ezylab.co.kr') {
        // 개발 환경에서 테스트를 위해 쿼리 파라미터로 서브도메인 설정 가능
        const params = new URLSearchParams(window.location.search);
        const testSubdomain = params.get('subdomain');
        
        if (testSubdomain) {
            if (testSubdomain === 'reset' || testSubdomain === 'clear') {
                sessionStorage.removeItem('linkflower_debug_subdomain');
                return null;
            }
            // 쿼리 파라미터가 있으면 세션 스토리지에 저장 (페이지 이동 시 유지를 위해)
            sessionStorage.setItem('linkflower_debug_subdomain', testSubdomain);
            return testSubdomain;
        }
        
        // 쿼리 파라미터가 없으면 세션 스토리지에서 확인
        const stored = sessionStorage.getItem('linkflower_debug_subdomain');
        return stored || null;
    }
    
    const parts = host.split('.');
    
    // 최소 3개 부분이 있어야 서브도메인 존재 (예: moky.linkflower.com)
    if (parts.length >= 3) {
        const firstPart = parts[0];
        // www는 서브도메인으로 처리하지 않음
        if (firstPart !== 'www') {
            return firstPart;
        }
    }
    
    return null;
};

export const SellerProvider = ({ children }) => {
  const [seller, setSeller] = useState(null);
  // 초기값을 함수로 전달하여 첫 렌더링 시점에 바로 계산 (Race Condition 방지)
  const [subdomain, setSubdomain] = useState(detectSubdomain);
  const [loading, setLoading] = useState(true);
  const [isSellerSite, setIsSellerSite] = useState(false);
  // 로그인한 판매자 본인의 테마 색상 (판매자 대시보드 /seller 에서 사용)
  const [myShopColors, setMyShopColors] = useState(null);

  const location = useLocation();
  const { isSeller } = useAuth();

  useEffect(() => {
    const loadSellerInfo = async () => {
      setLoading(true);
      
      // subdomain은 이미 useState 초기화에서 결정됨.
      // 변경이 필요하다면 setSubdomain을 호출하겠지만, 접속 도메인은 보통 안 바뀜.

      if (subdomain) {
        try {
          const sellerData = await getSellerBySubdomain(subdomain);
          setSeller(sellerData);
          setIsSellerSite(true);
          
          // 페이지 타이틀 변경
          document.title = sellerData.shopName || 'LinkFlower';
          
          // 파비콘 변경 (faviconUrl 우선, logoUrl 폴백)
          const faviconHref = sellerData.faviconUrl || sellerData.logoUrl;
          if (faviconHref) {
            const link = document.querySelector("link[rel~='icon']") || document.createElement('link');
            link.rel = 'icon';
            link.href = faviconHref;
            document.head.appendChild(link);
          }
        } catch (error) {
          // 존재하지 않는 상점 서브도메인 처리.
          // DNS 가 와일드카드(*.f26.kr)라 아무 서브도메인이나 서버에 도달하는데,
          // 여기서 본사 랜딩을 그대로 보여주면 잘못된 주소(asfdasdf.f26.kr 등)에서
          // 사이트가 "동작하는 것처럼" 보이고, 그 위에서 만든 링크들(샘플몰 등)이
          // 이중 서브도메인으로 계산되어 깨진다. 호스트 기반 접속이면 본사 도메인으로 보낸다.
          const host = window.location.hostname;
          const devHosts = ['localhost', '127.0.0.1', 'dev-linkflower.ezylab.co.kr'];
          const parts = host.split('.');
          if (!devHosts.includes(host) && parts.length >= 3) {
            const rootDomain = parts.slice(-2).join('.');
            window.location.replace(`${window.location.protocol}//${rootDomain}/`);
            return; // 리다이렉트 — 이후 상태 갱신 불필요
          }
          // 개발 환경(쿼리 파라미터 디버그)은 기존대로 본사 화면 폴백
          setIsSellerSite(false);
        }
      } else {
        setIsSellerSite(false);
      }
      
      setLoading(false);
    };

    loadSellerInfo();
  }, [subdomain]);

  // 로그인한 판매자 본인의 색상 로드 (판매자 대시보드 테마용)
  useEffect(() => {
    let cancelled = false;
    if (isSeller) {
      getMySellerInfo()
        .then((res) => {
          if (!cancelled) {
            setMyShopColors({
              primary: res.data?.primaryColor || null,
              secondary: res.data?.secondaryColor || null,
            });
          }
        })
        .catch(() => { /* 색상 로드 실패 시 기본색 유지 */ });
    } else {
      setMyShopColors(null);
    }
    return () => { cancelled = true; };
  }, [isSeller]);

  const value = {
    seller,
    subdomain,
    loading,
    isSellerSite,
    shopName: seller?.shopName || 'LinkFlower',
    logoUrl: seller?.logoUrl || null,
    primaryColor: seller?.primaryColor || '#13ec5b',
    secondaryColor: seller?.secondaryColor || '#f39c12',
  };

  // CSS 변수(테넌트 테마) 주입 — 화면(라우트)에 따라 적용 범위를 격리한다.
  //  - /admin  (플랫폼 관리자): 어떤 경우에도 테마 미적용, 항상 기본색
  //  - /seller (판매자 대시보드): 로그인한 판매자 본인의 색상
  //  - 그 외 고객 스토어프론트(서브도메인): 해당 판매자 샵의 색상
  // 테마를 documentElement 전역에 주입하므로, 비대상 화면에서는 반드시 기본값으로 되돌린다.
  useEffect(() => {
    const root = document.documentElement;
    const path = location.pathname;

    let colors = null;
    if (path.startsWith('/admin')) {
      colors = null; // 플랫폼 관리자: 테넌트 테마 격리
    } else if (path.startsWith('/seller')) {
      colors = myShopColors; // 판매자 대시보드: 본인 색상
    } else if (isSellerSite) {
      colors = { primary: value.primaryColor, secondary: value.secondaryColor }; // 고객 스토어프론트
    }

    if (colors && colors.primary) {
      applyThemeVars(root, colors.primary, colors.secondary || '#f39c12');
    } else {
      clearThemeVars(root); // index.css :root 기본값으로 복귀
    }
  }, [location.pathname, value.primaryColor, value.secondaryColor, isSellerSite, myShopColors]);

  return (
    <SellerContext.Provider value={value}>
      {children}
    </SellerContext.Provider>
  );
};

